import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon } from 'lucide-react';
import { Container, Section, Kicker, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { MeetingScheduler } from '../components/MeetingScheduler';
import { ASSESSMENT_QUESTIONS, DIMENSION_LABELS, scoreAssessment } from '../data/assessment';
import { submitLead, emailAssessmentResult } from '../lib/leads';
import { MAIL } from '../lib/config';
import { track } from '../lib/analytics';
type Stage = 'intro' | 'questions' | 'gate' | 'result';
export function Assessment() {
  const [stage, setStage] = React.useState<Stage>('intro');
  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, number>>({});
  const [email, setEmail] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [emailed, setEmailed] = React.useState<'sent' | 'preview' | 'failed'>('preview');
  const reduced = useReducedMotion();
  const question = ASSESSMENT_QUESTIONS[index];
  const answered = Object.keys(answers).length;
  const progress = Math.round(answered / ASSESSMENT_QUESTIONS.length * 100);
  const result = React.useMemo(() => scoreAssessment(answers), [answers]);
  const choose = (score: number) => {
    setAnswers(current => ({
      ...current,
      [question.id]: score
    }));
    window.setTimeout(() => {
      if (index === ASSESSMENT_QUESTIONS.length - 1) {
        setStage('gate');
      } else {
        setIndex(value => value + 1);
      }
    }, 180);
  };
  const reveal = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const outcome = await submitLead({
      email,
      organisation,
      consent,
      source: 'ai-readiness-assessment',
      line: 'ai'
    });
    setSubmitting(false);
    if (!outcome.ok) {
      setError(outcome.error ?? 'Something went wrong. Please try again.');
      return;
    }
    const sentResult = await emailAssessmentResult({
      email,
      organisation,
      total: result.total,
      band: result.band.name,
      byDimension: result.byDimension,
      nextSteps: result.band.nextSteps.map(step => ({
        title: step.title,
        packageName: step.packageName
      }))
    });
    setEmailed(sentResult.ok ? 'sent' : sentResult.simulated ? 'preview' : 'failed');
    track('assessment_completed', {
      score: result.total,
      band: result.band.name
    });
    setStage('result');
  };
  return <>
      <section className="surface-dark" data-id="element-621" data-mp="1i4mazpmfyywf">
        <Container className="py-14 sm:py-20" data-id="element-622" data-mp="1d748agz0pfwv">
          <Kicker data-id="element-623" data-mp="1d6b3hljmd0hd">Cavalla AI</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-624" data-mp="3ov96a9fj3jc7">
            AI readiness assessment
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-625" data-mp="3tubygtaetv2r">
            Twelve questions across data, skills, use cases, governance and budget. It takes about four minutes
            and gives you a score out of one hundred with three recommended next steps.
          </p>
        </Container>
      </section>

      <Section data-id="element-626" data-mp="cfx3audq9oq7">
        {stage === 'intro' && <div className="mx-auto max-w-2xl" data-id="element-627" data-mp="ge4mrwzkt453">
            <h2 className="font-display text-[28px] leading-tight text-ink" data-id="element-628" data-mp="gh0irfrv5iqw">Before you start</h2>
            <p className="mt-4 text-[16px] text-body" data-id="element-629" data-mp="lcfv23a836l7">
              Answer as your organisation is today, not as you intend it to be. The value of the score sits in its
              honesty. Where you are unsure, choose the lower option.
            </p>
            <ul className="mt-6 space-y-3 text-[15px] text-body" data-id="element-630" data-mp="2s5rpozf0ccvq">
              {Object.values(DIMENSION_LABELS).map(label => <li key={label} className="flex gap-3" data-id="element-631" data-mp="miiyp2n4vzk4">
                  <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-632" data-mp="hi4rtnx9qvno" />
                  {label}
                </li>)}
            </ul>
            <div className="mt-8" data-id="element-633" data-mp="rezmfvqf8bwo">
              <Button size="lg" onClick={() => setStage('questions')} data-id="element-634" data-mp="rgu7rb5lu61a">
                Begin the assessment
              </Button>
            </div>
          </div>}

        {stage === 'questions' && <div className="mx-auto max-w-2xl" data-id="element-635" data-mp="3uxsu5rwiq6ix">
            <div className="flex items-center justify-between text-small text-muted" data-id="element-636" data-mp="ci09dscsvqut">
              <span data-id="element-637" data-mp="7rjvk752i37x">
                Question {index + 1} of {ASSESSMENT_QUESTIONS.length}
              </span>
              <span data-id="element-638" data-mp="3g73fugnrgbau">{DIMENSION_LABELS[question.dimension]}</span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-mist" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Assessment progress" data-id="element-639" data-mp="3c5qccd854k73">
              <motion.div className="h-full rounded-full bg-gold" initial={false} animate={{
            width: `${Math.max(progress, 4)}%`
          }} transition={{
            duration: reduced ? 0 : 0.3,
            ease: 'easeOut'
          }} data-id="element-640" data-mp="2s3nfmrtsx1go" />
            </div>

            <motion.div key={question.id} initial={reduced ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: 'easeOut'
        }} className="mt-8" data-id="element-641" data-mp="3kqgknme84mwh">
              <fieldset data-id="element-642" data-mp="3frn5lpjagnu7">
                <legend className="font-display text-[24px] leading-snug text-ink sm:text-[28px]" data-id="element-643" data-mp="3fv17yxrycx1n">
                  {question.question}
                </legend>
                <div className="mt-6 space-y-3" data-id="element-644" data-mp="24euf0i28i5fq">
                  {question.options.map(option => {
                const selected = answers[question.id] === option.score;
                return <button key={option.label} type="button" onClick={() => choose(option.score)} aria-pressed={selected} className={`w-full rounded-sm border px-5 py-4 text-left text-[16px] transition-colors ${selected ? 'border-royal bg-mist text-ink' : 'border-line text-body hover:border-royal/40 hover:bg-mist/50'}`} data-id="element-645" data-mp="5wr7v1wlkd9r">
                        {option.label}
                      </button>;
              })}
                </div>
              </fieldset>
            </motion.div>

            {index > 0 && <button type="button" onClick={() => setIndex(value => value - 1)} className="mt-8 inline-flex items-center gap-2 text-[15px] text-royal underline-offset-4 hover:underline" data-id="element-646" data-mp="17mwdis1sdqht">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-647" data-mp="1ov724990k2js" />
                Previous question
              </button>}
          </div>}

        {stage === 'gate' && <div className="mx-auto max-w-xl rounded-md border border-line bg-champagne p-7 sm:p-9" data-id="element-648" data-mp="w6hxi08r1f3a">
            <Kicker data-id="element-649" data-mp="1152yi1vts2ss">Your result is ready</Kicker>
            <h2 className="mt-4 font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-650" data-mp="115uv9g15s6kn">
              Where should we send it
            </h2>
            <p className="mt-3 text-[15px] text-body" data-id="element-651" data-mp="1b391ynxa5gzr">
              We will show your score and next steps on this page straight away. Once email delivery is
              connected, a copy also arrives from {MAIL.from} so you can take it into a leadership discussion.
            </p>
            <form onSubmit={reveal} className="mt-7 space-y-5" data-id="element-652" data-mp="37mq1lzdlj215">
              <Field id="asm-email" label="Work email" required error={error ?? undefined} data-id="element-653" data-mp="37n9n1q2gg8jx">
                <input id="asm-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-654" data-mp="32p80uw9qft48" />
              </Field>
              <Field id="asm-org" label="Organisation" required data-id="element-655" data-mp="3mbw9scuew2l6">
                <input id="asm-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-656" data-mp="3mbcnyl1n4u1v" />
              </Field>
              <ConsentCheckbox id="asm-consent" checked={consent} onChange={setConsent} data-id="element-657" data-mp="25zgwm5bn2rl8">
                I am happy for Cavalla Solution to email me my result and related insights. See our{' '}
                <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-658" data-mp="gibwzhfnfp2l">
                  privacy notice
                </Link>
                .
              </ConsentCheckbox>
              <Button type="submit" size="lg" disabled={submitting} data-id="element-659" data-mp="6q5tbag1bj7k">
                {submitting ? 'Preparing your result' : 'Show my score'}
              </Button>
            </form>
          </div>}

        {stage === 'result' && <div className="mx-auto max-w-3xl" data-id="element-660" data-mp="2okng91x456v">
            <div className="rounded-md border border-line bg-mist p-7 sm:p-10" data-id="element-661" data-mp="2qf8dnd6vxay">
              <Kicker data-id="element-662" data-mp="3jy0tvxdrzpp7">Your result</Kicker>
              <div className="mt-5 flex flex-wrap items-end gap-6" data-id="element-663" data-mp="3jz23ryj2265c">
                <p className="font-display text-[64px] leading-none text-royal" data-id="element-664" data-mp="3owu0r6yk4ige">{result.total}</p>
                <div data-id="element-665" data-mp="1i6w476vekz7i">
                  <p className="text-small text-muted" data-id="element-666" data-mp="1i3i27zqnis0l">out of one hundred</p>
                  <p className="font-display text-[26px] leading-tight text-ink" data-id="element-667" data-mp="1n3e3vsbvm7cz">{result.band.name}</p>
                </div>
              </div>
              <p className="mt-6 max-w-prose text-[16px] text-body" data-id="element-668" data-mp="3b50vs6ws9keo">{result.band.description}</p>

              <p className="mt-6 flex items-start gap-2.5 text-[14px] text-muted" data-id="element-669" data-mp="2wg4sen5o8vke">
                <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-670" data-mp="2we0gbhxmcq91" />
                {emailed === 'sent' && <span data-id="element-671" data-mp="3p490tgfmnmqs">
                    A copy is on its way to <span className="text-ink" data-id="element-672" data-mp="3p5k0p55ycacm">{email}</span> from {MAIL.from}. If it
                    does not arrive, please check your spam folder.
                  </span>}
                {emailed === 'failed' && <span data-id="element-673" data-mp="3k5fm5koltpp1">
                    We could not email your copy just now. Your full result is shown below, and we will follow it
                    up with you directly.
                  </span>}
                {emailed === 'preview' && <span data-id="element-674" data-mp="28ofumra2zew2">
                    Email delivery is not connected on this preview, so nothing has been sent. Your full result
                    is shown below.
                  </span>}
              </p>

              <div className="mt-8 space-y-4" data-id="element-675" data-mp="ztc1oyvykpfr">
                {result.byDimension.map(entry => {
              const percentage = Math.round(entry.score / entry.max * 100);
              return <div key={entry.dimension} data-id="element-676" data-mp="81mnxdxmepi9">
                      <div className="flex items-baseline justify-between text-[14px]" data-id="element-677" data-mp="80bjrd09jfqm">
                        <span className="text-ink" data-id="element-678" data-mp="355zsjqf8b56">{DIMENSION_LABELS[entry.dimension]}</span>
                        <span className="text-muted" data-id="element-679" data-mp="gwaq2v7wfr2u">{percentage}%</span>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-line" data-id="element-680" data-mp="c2g7jv415i5c">
                        <motion.div className="h-full rounded-full bg-gold" initial={reduced ? false : {
                    width: 0
                  }} animate={{
                    width: `${percentage}%`
                  }} transition={{
                    duration: reduced ? 0 : 0.6,
                    ease: 'easeOut'
                  }} data-id="element-681" data-mp="3ki9sqn9b3hsq" />
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <h2 className="mt-12 font-display text-[26px] text-ink" data-id="element-682" data-mp="1y6rbr1qpmwke">Three recommended next steps</h2>
            <ol className="mt-6 space-y-4" data-id="element-683" data-mp="10jskj6isegtw">
              {result.band.nextSteps.map((step, stepIndex) => <li key={step.title} className="rounded-md border border-line p-6" data-id="element-684" data-mp="15ilmc1opoje8" data-mpi={stepIndex}>
                  <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-685" data-mp="1agnerd811v2d">
                    <div data-id="element-686" data-mp="1fgjbcreeak7w">
                      <p className="font-display text-[13px] text-gold" data-id="element-687" data-mp="1fgrp0licdtk5">0{stepIndex + 1}</p>
                      <h3 className="mt-2 font-display text-[21px] leading-snug text-ink" data-id="element-688" data-mp="2lv0rpn18sq3m">{step.title}</h3>
                      <p className="mt-2 max-w-prose text-[15px] text-body" data-id="element-689" data-mp="2qtu2v8t2450m">{step.detail}</p>
                    </div>
                    <div className="shrink-0" data-id="element-690" data-mp="f70cxsd0xvi7">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted" data-id="element-691" data-mp="a86z1ytts2hi">
                        Engagement
                      </p>
                      <p className="mt-1 text-[15px] font-medium text-royal" data-id="element-692" data-mp="5fxbgpg6neie">{step.packageName}</p>
                    </div>
                  </div>
                </li>)}
            </ol>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-693" data-mp="3rn425rmj8bht">
              <Button to="/services/ai" variant="secondary" size="lg" data-id="element-694" data-mp="3nmtr40jvuuw3">
                Read about Cavalla AI
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-695" data-mp="3ip1zz8r2bcsy" />
              </Button>
            </div>

            <div className="mt-12 rounded-md border border-line p-6 sm:p-8" data-id="element-696" data-mp="3drjsznwlv7nl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-697" data-mp="1gwnclu5r1g55">Next step</p>
              <h2 className="mt-4 font-display text-[24px] leading-snug text-ink sm:text-[28px]" data-id="element-698" data-mp="1bylwni3mdwxw">
                Take your score into a conversation
              </h2>
              <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-699" data-mp="dmj6wsn5rxpy">
                Thirty minutes with our founder, who will have your result to hand. Choose a slot that suits
                your time zone.
              </p>
              <MeetingScheduler line="ai" className="mt-6" data-id="element-700" data-mp="4k094my8cbak" />
            </div>

            <Callout className="mt-8" data-id="element-701" data-mp="3m107pfauz5mz">
              This score is an indicative guide produced from your own answers. It is not an audit, and Cavalla
              Solution is not a financial adviser.
            </Callout>
          </div>}
      </Section>
    </>;
}